import { ChevronDownIcon, ChevronRightIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Divider,
    Flex,
    Input,
    InputGroup,
    InputRightAddon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Select,
    SkeletonText,
    Spinner,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useBreakpointValue,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import CatalogueSelector from 'components/adspresso/CatalogueSelector';
import currencySymbolMap from 'currency-symbol-map';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { GiStarShuriken } from "react-icons/gi";
import { useUpdateAdspressoCampaignMutation } from 'store/adspresso/adspressoCampaignApi';
import { useGetCataloguesQuery } from 'store/campaigns/campaignsApi';
import { bidStrategiesForLeads, bidStrategiesForSales, budgetOptions } from 'utils/campaignOptions';
import * as Yup from 'yup';

function CampaignForm({ isLoadingCampaign, campaignData, adsetsData, onSuccess }) {

    const bgColor = useColorModeValue('inherit', '#231f20');
    const hoverBgColor = useColorModeValue('gray.100', 'gray.700');
    const textColor = useColorModeValue('gray.800', 'gray.200');
    const background = useColorModeValue('gray.50', 'gray.800');
    const borderColor = useColorModeValue("gray.200", "purple.800");
    const shouldTruncate = useBreakpointValue({ base: false, md: true});
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isBidModalOpen, onOpen: onBidModalOpen, onClose: onBidModalClose } = useDisclosure();
    const toast = useToast();
    const [adsetBidAmounts, setAdsetBidAmounts] = useState({});
    const { name, objective, adsAccounts, _id } = campaignData || {};
    const selectedAdAccountIds = adsAccounts?.map(account => account.adsAccountId) || [];

    const [updateCampaign, { isLoading: isCampaignUpdating }] = useUpdateAdspressoCampaignMutation({});

    useEffect(() => {
        if (campaignData) {
            formik.setValues({
                name: campaignData?.name,
                objective: campaignData?.objective,
                isAdvantageCampaignEnabled: campaignData?.bid_strategy ? true : false,
                isAdScheduled: campaignData?.pacing_type && campaignData?.pacing_type.includes("day_parting") ? true : false,
                accelerated: campaignData?.pacing_type && campaignData?.pacing_type.includes("no_pacing") ? true : false,
                budget: campaignData?.daily_budget ? 'daily_budget' : 'lifetime_budget',
                bid_strategy: campaignData?.bid_strategy ? campaignData?.bid_strategy : 'LOWEST_COST_WITHOUT_CAP',
                daily_budget: campaignData?.daily_budget ? Number(campaignData?.daily_budget) / 100 : 800,
                lifetime_budget: campaignData?.lifetime_budget ? Number(campaignData?.lifetime_budget) / 100 : 14000,
                currency: campaignData?.currency,
                isAdvantageCatalogueEnabled: (campaignData?.promoted_object &&  campaignData?.promoted_object?.length > 0) ? true : false,
                product_catalog_id: (campaignData?.promoted_object && campaignData?.promoted_object?.length > 0) ? campaignData?.promoted_object[0]?.product_catalog_id : '',
            });
        }
    }, [campaignData]);

    useEffect(() => {
        if (adsetsData) {

            const initialBids = {};
            adsetsData?.data?.forEach(adSet => {
                initialBids[adSet?._id] = 5;
            });
            setAdsetBidAmounts(initialBids);
        }
    }, [adsetsData]);

    const formik = useFormik({
        initialValues: {
            name: '',
            objective: '',
            isAdvantageCampaignEnabled: false,
            isAdvantageCatalogueEnabled: false,
            product_catalog_id: '',
            isAdScheduled: false,
            accelerated: false,
            budget: 'daily_budget',
            bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
            daily_budget: 800,
            lifetime_budget: 14000,
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Campaign name is required'),
            daily_budget: Yup.number().when('budget', {
                is: 'daily_budget',
                then: schema => schema.required('Daily budget is required').min(1, 'Daily budget must be a positive number'),
                otherwise: schema => schema.notRequired(),
            }),

            lifetime_budget: Yup.number().when('budget', {
                is: 'lifetime_budget',
                then: schema => schema.required('Lifetime budget is required').min(1, 'Lifetime budget must be a positive number'),
                otherwise: schema => schema.notRequired(),
            }),
        }),
        onSubmit: (values) => {
            const { name, budget, daily_budget, currency, lifetime_budget, accelerated, isAdScheduled, isAdvantageCampaignEnabled, bid_strategy } = values;

            let payload = {
                name,
            };

            if (!isAdvantageCampaignEnabled) {
                // CASE 1: Not using Advantage Campaign
                const adset_budgets = [];

                if (Array.isArray(campaignData?.adsets)) {
                    campaignData.adsets.forEach((adsetId) => {
                        const budgetValue = campaignData?.daily_budget || campaignData?.lifetime_budget;
                        const isDailyBudget = !!campaignData?.daily_budget;

                        if (adsetId && budgetValue) {
                            adset_budgets.push({
                                adset_id: adsetId,
                                [isDailyBudget ? 'daily_budget' : 'lifetime_budget']: Number(budgetValue)
                            });
                        }

                    });
                }

                if (adset_budgets.length > 0) {
                    payload.adset_budgets = adset_budgets;
                }

                if (budget === 'lifetime_budget' && campaignData?.lifetime_budget) {
                    payload.lifetime_budget = Number(lifetime_budget) * 100;
                    payload.currency = currency;
                }

                if (budget === 'daily_budget' && campaignData?.daily_budget) {
                    payload.daily_budget = Number(daily_budget) * 100;
                    payload.currency = currency;
                }

                if (bid_strategy && campaignData?.bid_strategy) {
                    payload.bid_strategy = bid_strategy;
                }

                if (isAdScheduled) {
                    payload.pacing_type = ["day_parting"];
                }

            } else {
                // CASE 2: Advantage Campaign Flow

                if (budget === 'lifetime_budget') {
                    payload.lifetime_budget = Number(lifetime_budget) * 100;
                    payload.currency = currency;
                }

                if (budget === 'daily_budget') {
                    payload.daily_budget = Number(daily_budget) * 100;
                    payload.currency = currency;
                }

                if (accelerated) {
                    payload.pacing_type = ["no_pacing"];
                    if (isAdScheduled) {
                        payload.pacing_type.push("day_parting");
                    }
                } else {
                    payload.pacing_type = ["standard"];
                    if (isAdScheduled) {
                        payload.pacing_type.push("day_parting");
                    }
                }

                if (!campaignData?.bid_strategy) {
                    payload.advantage_budget = true;
                }

                // Only add bid_strategy & adset_bid_amounts if it changed
                if (
                    bid_strategy &&
                    campaignData?.bid_strategy !== undefined && 
                    bid_strategy !== campaignData.bid_strategy
                ) {
                    payload.bid_strategy = bid_strategy;

                    // Only set adset_bid_amounts if bid_strategy is NOT "LOWEST_COST_WITHOUT_CAP"
                    if (bid_strategy !== "LOWEST_COST_WITHOUT_CAP") {
                        payload.adset_bid_amounts = {};

                        Object.entries(adsetBidAmounts).forEach(([adSetId, value]) => {
                            payload.adset_bid_amounts[adSetId] = value * 100;
                        });
                    } else {

                        payload.adset_bid_amounts = {};

                        Object.entries(adsetBidAmounts).forEach(([adSetId, value]) => {
                            payload.adset_bid_amounts[adSetId] = null;
                        });
                    }
                }else{
                     payload.bid_strategy = bid_strategy;
                }
            }

            updateCampaign({
                campaignId: _id,
                payload
            }).unwrap()
                .then(() => {
                    onSuccess();
                    toast({
                        position: "top-right",
                        title: "Campaign updated",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    onClose();
                })
                .catch((error) => {
                    toast({
                        position: "top-right",
                        title: "Error updating campaign",
                        status: "error",
                        description: error?.data?.message,
                        duration: 4000,
                        isClosable: true,
                    });
                    console.error('Error updating campaign:', error);
                });
        }
    });

    const { data: dataCatalogues, error: errorCatalogues, isLoading: isLoadingCatalogues } = useGetCataloguesQuery(undefined, { skip: !formik?.values?.isAdvantageCatalogueEnabled || !isOpen });

    const handleBudgetChange = (e) => {
        const selectedBudgetType = e.target.value;
        if (selectedBudgetType === 'daily_budget') {
            formik.setFieldValue('lifetime_budget', Number(formik.values.lifetime_budget));
            formik.setFieldValue('daily_budget', Number(formik.values.daily_budget));
        } else if (selectedBudgetType === 'lifetime_budget') {
            formik.setFieldValue('daily_budget', Number(formik.values.daily_budget));
            formik.setFieldValue('lifetime_budget', Number(formik.values.lifetime_budget));
        }
        formik.setFieldValue('budget', selectedBudgetType);
    };

    const getObjectiveName = (objective) => {
        switch (objective) {
            case 'OUTCOME_SALES':
                return 'Sales';
            case 'OUTCOME_LEADS':
                return 'Leads';
            case 'OUTCOME_APP_PROMOTION':
                return 'App promotion';
            case 'OUTCOME_AWARENESS':
                return 'Awareness';
            case 'OUTCOME_ENGAGEMENT':
                return 'Engagement';
            case 'OUTCOME_TRAFFIC':
                return 'Traffic';
            default:
                return 'Unknown Objective';
        }
    };

    const handleClose = () => {
        onClose();
    };

    const isBudgetChanged = (() => {
        if (!campaignData?.bid_strategy) return false;

        const originalDaily = Number(campaignData.daily_budget) / 100;
        const originalLifetime = Number(campaignData.lifetime_budget) / 100;

        return (
            (formik.values.budget === 'daily_budget' && Number(formik.values.daily_budget) !== originalDaily) ||
            (formik.values.budget === 'lifetime_budget' && Number(formik.values.lifetime_budget) !== originalLifetime) ||
            !campaignData?.adsets?.length > 0
        );
    })();

    return (
            <Box
                height="100%"
                p="20px"
                px="20px"
                borderRight={"1px solid"}
                borderBottom={{ base: "1px solid", md: "none" }}
                borderColor={borderColor}   
                overflowY="auto"
            >

            {isLoadingCampaign ? (
                <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'70vh'}>
                    <Spinner />
                    <Text>Loading…</Text>
                </Flex>
            ) : (
                <Flex gap={4} h={'full'}>
                    <Flex direction="column" borderRadius="md" borderWidth={1} borderColor={borderColor} flex={0.5}>
                        <Box p={'10px'}>
                            {/* Campaign Name */}
                            {name ? (
                                <Flex
                                    alignItems="center"
                                    width="100%"
                                    borderRadius="md"
                                >
                                    <Text
                                        fontWeight="bold"
                                        color={textColor}
                                        fontSize={'14px'}
                                    >
                                        {name}
                                    </Text>
                                </Flex>
                            ) : (
                                <Flex
                                    alignItems="center"
                                >
                                    <Text
                                        fontWeight="normal"
                                        color={textColor}
                                        fontSize={'14px'}
                                    >
                                        Campaign Name not Available
                                    </Text>
                                </Flex>
                            )}

                            {/* Campaign Objective */}
                            {objective ? (
                                <Flex
                                    alignItems="center"
                                >
                                    <Text
                                        fontWeight="normal"
                                        color={textColor}
                                        fontSize={'12px'}
                                    >
                                        {getObjectiveName(objective)}
                                    </Text>
                                </Flex>
                            ) : (
                                <Flex
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Text
                                        fontWeight="normal"
                                        color={textColor}
                                    >
                                        Objective not Available
                                    </Text>
                                </Flex>
                            )}
                        </Box>
                        
                        <Divider />
                        {/* Ad Accounts */}
                        <Box p={'10px'} width={'full'}>
                            {adsAccounts && adsAccounts.length > 0 ? (
                                <Flex direction="column">
                                    <Text mb={2} fontSize={'14px'}>Selected Ad Accounts</Text>
                                    <CheckboxGroup value={selectedAdAccountIds} name="adsAccountName">
                                    <Flex direction="column">
                                        {adsAccounts.map((account) => {
                                        const isLong = account.adsAccountName.length > 40;
                                        const showTruncate = shouldTruncate && isLong;                               

                                            return (
                                            <Flex key={account.adsAccountId} direction="column">
                                                {showTruncate ? (
                                                <Tooltip label={account.adsAccountName} placement="top" hasArrow p={2} fontSize={'12px'}>
                                                    <Text fontSize="14px" fontWeight="bold" isTruncated={showTruncate}  maxWidth={showTruncate ? '400px' : '100%'}>
                                                        {account.adsAccountName}
                                                    </Text>
                                                </Tooltip>
                                                ) : (
                                                <Text fontSize="14px" fontWeight="bold">
                                                    {account.adsAccountName}
                                                </Text>
                                                )}
                                                <Text fontSize="12px" color="gray.500">
                                                {account.adsAccountId}
                                                </Text>
                                            </Flex>
                                            );
                                        })}
                                        </Flex>
                                    </CheckboxGroup>
                                </Flex>
                            ) : (
                                <Flex
                                    direction="column"
                                >
                                    <Text mb={2} fontWeight="bold" color={textColor}>
                                        Ad Accounts not Available
                                    </Text>
                                </Flex>
                            )}
                        </Box>
                    </Flex>
                    <Flex maxH={'580px'} overflowY={'auto'} px={4}>
                        <form onSubmit={formik.handleSubmit}>
                            <Box width="100%" bg={background} p={4} borderRadius="md">
                                <Flex direction="column" gap={4}>
                                    <Box>
                                        <Flex align="center">
                                            <Text mb={2} fontWeight={'bold'}>
                                                Campaign Name
                                            </Text>
                                        </Flex>
                                        <Input
                                            placeholder="Enter campaign name"
                                            bg="#fff"
                                            name="name"
                                            size={'sm'}
                                            borderRadius="md"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                        {formik.touched.name && formik.errors.name && (
                                            <Text fontSize={'12px'} color="red.500" mt={1}>{formik.errors.name}</Text>
                                        )}
                                    </Box>

                                    <Box>
                                        <Flex align="center">
                                            <Text mb={2} fontWeight={'bold'}>
                                                Objective
                                            </Text>
                                        </Flex>
                                        <Select
                                            name="type"
                                            borderRadius="md"
                                            value={formik.values.objective}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            size="sm"
                                            isDisabled
                                        >
                                            <option value="OUTCOME_LEADS">Leads</option>
                                            <option value="OUTCOME_SALES">Sales</option>
                                            <option value="OUTCOME_AWARENESS">Awareness</option>
                                            <option value="OUTCOME_APP_PROMOTION">App promotion</option>
                                            <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                                            <option value="OUTCOME_TRAFFIC">Traffic</option>
                                        </Select>
                                    </Box>

                                    {/* Advantage Catalogue section */}
                                    {formik?.values.objective === 'OUTCOME_SALES'  ?
                                        <Box>
                                            <Flex direction='column'>
                                                <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                                    <Text fontWeight="bold">Advantage+ catalogue ads</Text>
                                                    <GiStarShuriken />
                                                    </Box>

                                                    <Box display={'flex'} alignItems={'center'} gap={2}>
                                                    <Text>{formik?.values.isAdvantageCatalogueEnabled ? 'On' : 'Off'}</Text>
                                                    
                                                    <Tooltip 
                                                        label="Your catalog can't be changed after your campaign has been created. Create a new campaign to use a different catalog." 
                                                        hasArrow 
                                                        p={2}
                                                        fontSize="sm"
                                                        borderRadius={5}
                                                        placement="right"
                                                    >
                                                        <Switch
                                                            colorScheme='brand'
                                                            isDisabled
                                                            isChecked={formik?.values.isAdvantageCatalogueEnabled}
                                                            onChange={() =>
                                                                formik?.setFieldValue('isAdvantageCatalogueEnabled', !formik.values.isAdvantageCatalogueEnabled)
                                                            }
                                                        />
                                                    </Tooltip>
                                                    </Box>
                                                </Flex>

                                                <Text fontSize={'12px'} textAlign={'justify'}>
                                                    Drive sales using your product information by showing relevant products to the right people.
                                                </Text>
                                            </Flex>

                                            {formik?.values?.isAdvantageCatalogueEnabled ? 
                                                <Box py={2}>
                                                    {isLoadingCatalogues ? (
                                                        <Flex>
                                                            <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                                                        </Flex>
                                                    ) : dataCatalogues?.data?.length > 0 ? (
                                                        <Box>
                                                            <CatalogueSelector
                                                                prefilledId={formik.values.product_catalog_id}
                                                                dataCatalogues={dataCatalogues}
                                                                context={'update'}
                                                            />
                                                        </Box>
                                                    ):null}
                                                </Box>
                                            :null}
                                        </Box>
                                    :null}
                                    
                                    {/* Advantage campaign section */}
                                    {campaignData?.smart_promotion_type === 'AUTOMATED_SHOPPING_ADS' ? null :
                                        <Box>
                                            <Flex alignItems={'center'} justifyContent={'space-between'} mb={2}>
                                                <Box display={'flex'} alignItems={'center'} gap={1}>
                                                    <Text fontWeight="bold">Advantage campaign budget</Text>
                                                    <GiStarShuriken />
                                                </Box>

                                                <Box display={'flex'} alignItems={'center'} gap={2}>
                                                    <Text>{formik.values.isAdvantageCampaignEnabled ? 'On' : 'Off'}</Text>

                                                    <Tooltip
                                                        borderRadius={5}
                                                        placement="right"
                                                        isDisabled={!!(campaignData?.adsets?.length > 0)}
                                                        label={
                                                            <Box fontSize="sm" p="5px">
                                                                <Text>
                                                                    To turn off Advantage campaign budget, you must create an ad set in this campaign.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                        hasArrow
                                                    >
                                                        <Box display="inline-block">
                                                            <Switch
                                                                colorScheme="brand"
                                                                isDisabled={!campaignData?.adsets?.length}
                                                                isChecked={formik.values.isAdvantageCampaignEnabled}
                                                                onChange={() =>
                                                                    formik.setFieldValue(
                                                                        'isAdvantageCampaignEnabled',
                                                                        !formik.values.isAdvantageCampaignEnabled
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                            </Flex>
                                            <Text fontSize={'12px'} textAlign={'justify'}>Distribute your budget across ad sets to get more results. You can control spending for each ad set.</Text>
                                        </Box>
                                    }

                                    {formik.values.isAdvantageCampaignEnabled ? (
                                        <>
                                            <Flex my={2} direction={'column'} gap={2}>
                                                {/* Budget */}
                                                <Flex alignItems={'center'}>
                                                    <Text fontWeight="bold">Budget</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text >
                                                                    If you choose a daily budget, you may spend more on some days and less on others but will maintain an average daily budget. If you choose a lifetime budget, we'll never spend more than that amount.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>
                                                <Box>
                                                    <Flex gap={1}>
                                                        <Select
                                                            name="budget"
                                                            value={formik.values.budget}
                                                            onChange={handleBudgetChange}
                                                            onBlur={formik.handleBlur}
                                                            bg="#fff"
                                                            borderRadius={'md'}
                                                            maxW={'200px'}
                                                            size={'sm'}
                                                            isDisabled={campaignData?.bid_strategy}
                                                        >
                                                            {budgetOptions?.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                        {formik.values.budget === 'daily_budget' ? (
                                                            <InputGroup>
                                                                <Input
                                                                    name="daily_budget"
                                                                    placeholder={currencySymbolMap(formik?.values?.currency)}
                                                                    bg="#fff"
                                                                    type="number"
                                                                    value={String(formik.values.daily_budget)}
                                                                    onChange={(e) => formik.setFieldValue('daily_budget', e.target.value)}
                                                                    onBlur={formik.handleBlur}
                                                                    borderRadius={'md'}
                                                                    size={'sm'}
                                                                />
                                                                <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                                    {formik?.values?.currency}
                                                                </InputRightAddon>
                                                            </InputGroup>
                                                        ) : null}
                                                        {formik.values.budget === 'lifetime_budget' ? (
                                                            <InputGroup>
                                                                <Input
                                                                    name="lifetime_budget"
                                                                    placeholder={currencySymbolMap(formik?.values?.currency)}
                                                                    bg="#fff"
                                                                    type="number"
                                                                    value={String(formik.values.lifetime_budget)}
                                                                    onChange={(e) => formik.setFieldValue('lifetime_budget', e.target.value)}
                                                                    onBlur={formik.handleBlur}
                                                                    borderRadius={'md'}
                                                                    size={'sm'}
                                                                />
                                                                <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                                    {formik?.values?.currency}
                                                                </InputRightAddon>
                                                            </InputGroup>
                                                        ) : null}
                                                    </Flex>

                                                    <Flex>
                                                        {formik.values.budget === 'daily_budget' && formik.touched.daily_budget && formik.errors.daily_budget && (
                                                            <Text fontSize={'12px'} color="red.500">{formik.errors.daily_budget}</Text>
                                                        )}
                                                        {formik.values.budget === 'lifetime_budget' && formik.touched.lifetime_budget && formik.errors.lifetime_budget && (
                                                            <Text fontSize={'12px'} color="red.500">{formik.errors.lifetime_budget}</Text>
                                                        )}
                                                    </Flex>
                                                </Box>
                                                <Box display={'flex'} flexDirection='column' gap='2'>
                                                    <Flex alignItems={'center'}>
                                                        <Text fontWeight="bold">Campaign bid strategy</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p={'5px'}>
                                                                    <Text >
                                                                        Cost per result goal, ROAS goal or bid caps allow you to enter a specific amount later for each ad set.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Flex>

                                                    <Flex>
                                                        <Text fontSize={'12px'}>How we'll bid in ad auctions.</Text>
                                                    </Flex>

                                                    <Flex>
                                                        <Menu>
                                                            <Tooltip
                                                                label='You cannot switch the bid strategy because your delivery type is set to "accelerated", which is only available with the lowest cost with bid cap bid strategy. Please change your delivery type to standard to change your bid strategy.'
                                                                isDisabled={!(campaignData?.pacing_type?.includes("no_pacing") ?? false)}
                                                                hasArrow
                                                                placement="top"
                                                                bg="gray.700"
                                                                color="white"
                                                                fontSize="12px"
                                                                p={3}
                                                                maxW="300px"
                                                            >
                                                            <MenuButton
                                                                as={Button}
                                                                borderRadius="md"
                                                                p={2}
                                                                bg={isBudgetChanged ? 'gray.50' : '#fff'}
                                                                cursor={isBudgetChanged ? "not-allowed" : "pointer"}
                                                                _hover={{ bg: isBudgetChanged ? 'gray.50' : '#fff' }}
                                                                w='auto'
                                                                fontSize='12px'
                                                            >
                                                                <Flex alignItems={'center'} justifyContent="space-between" gap='2'>
                                                                    <Text color={isBudgetChanged ? 'gray.500' : 'none'}>
                                                                        {formik.values.bid_strategy
                                                                            ? (formik.values.objective === 'OUTCOME_SALES'
                                                                                ? bidStrategiesForSales.find(option => option.value === formik.values.bid_strategy)?.label
                                                                                : formik.values.objective === 'OUTCOME_LEADS'
                                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                                    : formik.values.objective === 'OUTCOME_APP_PROMOTION'
                                                                                        ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label
                                                                                        : formik.values.objective === 'OUTCOME_AWARENESS'
                                                                                            ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                                            : formik.values.objective === 'OUTCOME_ENGAGEMENT'
                                                                                                ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                                                : formik.values.objective === 'OUTCOME_TRAFFIC'
                                                                                                    ? bidStrategiesForLeads.find(option => option.value === formik.values.bid_strategy)?.label 
                                                                                        : "Select a bid strategy")
                                                                            : "Select a bid strategy"}
                                                                    </Text>
                                                                    <ChevronDownIcon boxSize='5' />
                                                                </Flex>
                                                            </MenuButton>
                                                            </Tooltip>

                                                            {isBudgetChanged ? null : (
                                                                <MenuList zIndex={20}>
                                                                    {(formik.values.objective === 'OUTCOME_SALES' ? bidStrategiesForSales : bidStrategiesForLeads).map((option) => {
                                                                        const isBidCap = option.value === 'LOWEST_COST_WITH_BID_CAP';

                                                                        const MenuItemContent = (
                                                                            <Box
                                                                                display="flex"
                                                                                justifyContent="space-between"
                                                                                width="100%"
                                                                                alignItems="center"
                                                                                py={2}
                                                                                px={2}
                                                                            >
                                                                                <Box>
                                                                                    <Text fontWeight="medium" fontSize="12px">{option.label}</Text>
                                                                                    <Text fontSize="12px">{option.description}</Text>
                                                                                </Box>
                                                                                {isBidCap && campaignData?.bid_strategy !== 'LOWEST_COST_WITH_BID_CAP' && (
                                                                                    <ChevronRightIcon boxSize='5' />
                                                                                )}
                                                                            </Box>
                                                                        );

                                                                        return (isBidCap && campaignData?.bid_strategy !== 'LOWEST_COST_WITH_BID_CAP') ? (
                                                                            <Popover trigger="hover" placement="right-start" closeOnBlur={false} key={option.value}>
                                                                                <PopoverTrigger>
                                                                                    <Box width="100%">
                                                                                        <MenuItem
                                                                                            _hover={{ bg: 'gray.50' }}
                                                                                            p={0}
                                                                                        >
                                                                                            {MenuItemContent}
                                                                                        </MenuItem>
                                                                                    </Box>
                                                                                </PopoverTrigger>
                                                                                <PopoverContent
                                                                                    cursor='pointer'
                                                                                    width="auto"
                                                                                    onMouseEnter={(e) => e.stopPropagation()}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        formik.setFieldValue('bid_strategy', option.value);
                                                                                        onBidModalOpen();
                                                                                    }}
                                                                                >
                                                                                    <PopoverArrow />
                                                                                    <PopoverBody>
                                                                                        <Text fontWeight="semibold" fontSize='14px'>Bid cap</Text>
                                                                                        <Text fontSize="12px">
                                                                                            Set the highest that you want to bid in any auction.
                                                                                        </Text>
                                                                                    </PopoverBody>
                                                                                </PopoverContent>
                                                                            </Popover>
                                                                        ) : (
                                                                            <MenuItem
                                                                                key={option.value}
                                                                                onClick={() => {
                                                                                    formik.setFieldValue('bid_strategy', option.value);
                                                                                    if (
                                                                                        campaignData?.bid_strategy === 'LOWEST_COST_WITHOUT_CAP' &&
                                                                                        option.value === 'LOWEST_COST_WITHOUT_CAP'
                                                                                    ) {
                                                                                        return;
                                                                                    } else if (campaignData?.bid_strategy !== option.value && option.value !== 'LOWEST_COST_WITHOUT_CAP') {
                                                                                        onBidModalOpen();
                                                                                    }
                                                                                }}
                                                                                _hover={{ bg: background }}
                                                                                py={2}
                                                                                flexDirection="column"
                                                                                alignItems="flex-start"
                                                                            >
                                                                                <Text fontWeight="medium" fontSize="12px">{option.label}</Text>
                                                                                <Text fontSize="12px">{option.description}</Text>
                                                                            </MenuItem>
                                                                        );
                                                                    })}
                                                                </MenuList>
                                                            )}
                                                        </Menu>
                                                    </Flex>

                                                    <Flex alignItems={'center'}>
                                                        <Text fontSize={'10px'}>Not all bid strategies are available for all campaigns.</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="12px" p={'5px'}>
                                                                    <Text >
                                                                        The highest-volume bid strategy is only available if all ad sets are using the same delivery optimisation.<br></br>
                                                                        Cost per result goal is only available for some delivery optimisations.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Flex>
                                                </Box>
                                            </Flex>

                                            <Divider />

                                            <Flex direction='column' gap={1}>
                                                <Flex alignItems={'center'}>
                                                    <Text fontWeight="bold">Ad scheduling</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text >
                                                                    You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>
                                                <Text fontSize='12px'>You'll set the specific schedule within each ad set.</Text>
                                                <Flex>
                                                    <Checkbox
                                                        size={'md'}
                                                        colorScheme='brand'
                                                        isChecked={formik.values.isAdScheduled}
                                                        onChange={() => formik.setFieldValue('isAdScheduled', !formik.values.isAdScheduled)}
                                                        isDisabled={formik.values.budget !== 'lifetime_budget'}
                                                    >
                                                        <Flex direction='column'>
                                                            <Text fontSize='12px'>Run ads on a schedule</Text>
                                                            {formik.values.budget !== 'lifetime_budget' ?
                                                                <Text fontSize='10px'>Ad scheduling is only available when you use lifetime budget.</Text>
                                                                : null}
                                                        </Flex>
                                                    </Checkbox>
                                                </Flex>
                                            </Flex>

                                            {formik.values.bid_strategy === 'LOWEST_COST_WITH_BID_CAP' ? 
                                                <Flex direction="column" gap={2}>
                                                    <Flex align={'center'} mt={'10px'}>
                                                        <Text fontWeight="bold">Delivery type</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Flex direction={'column'} fontSize="12px" p={'5px'}>
                                                                    <Text>Standard delivery uses pacing to control your spend.</Text>
                                                                    <Text>Pacing keeps you from spending your budget too quickly and is the recommended option for most advertisers.</Text>
                                                                    <Text>Accelerated delivery can be useful for time-sensitive promotions. You need to set a bid cap to use accelerated delivery.</Text>
                                                                </Flex>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Flex>
                                                    {/* Added Checkbox for Accelerated delivery */}
                                                    <Flex gap={2}>
                                                        <Checkbox
                                                            colorScheme={"brand"}
                                                            name="accelerated"
                                                            size={'md'}
                                                            isChecked={formik.values.accelerated}
                                                            onChange={formik.handleChange}
                                                        />
                                                        <Flex direction="column">
                                                            <Text fontSize="14px" fontWeight="bold">Accelerated</Text>
                                                            <Text fontSize="12px">Spend your budget and get results as quickly as possible</Text>
                                                        </Flex>
                                                    </Flex>
                                                </Flex>
                                            :null}
                                        </>
                                    ) : (null)}
                                </Flex>
                            </Box>

                            {/* Submit Button in a separate Flex container */}
                            <Flex mt={4}>
                                <Button
                                    colorScheme="brand"
                                    isLoading={isCampaignUpdating}
                                    isDisabled={!formik.isValid}
                                    type="submit"
                                    size={'sm'}
                                    mb={'10px'}
                                >
                                    Save
                                </Button>
                            </Flex>
                        </form>
                    </Flex>
                </Flex>
            )}

            <Modal isOpen={isBidModalOpen} onClose={onBidModalClose} scrollBehavior="inside" isCentered size="2xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader p={2} fontSize="md">Set bid caps for 3 ad sets</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody borderTop="1px" borderColor="gray.100">
                        <Text fontSize="12px" mb={4}>
                            Set bid caps for all of the ad sets (published and inactive) in this campaign.
                        </Text>

                        <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th fontSize="12px">Ad set name</Th>
                                        <Th fontSize="12px">{formik.values.bid_strategy === 'LOWEST_COST_WITH_BID_CAP' ? 'Bid cap' : formik.values.bid_strategy === 'COST_CAP' ? 'Cost per result goal' : 'ROAS goal'}</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {adsetsData?.data?.map((adSet, _id) => (
                                        <Tr key={_id}>
                                            <Td fontSize="12px" borderRight={'2px'} borderRightColor={'green.500'}>{adSet.name}</Td>
                                            <Td fontSize="12px">
                                                <Input
                                                    placeholder={currencySymbolMap(formik?.values?.currency)}
                                                    size="sm"
                                                    width="80px"
                                                    value={adsetBidAmounts[adSet?._id]}
                                                    type="number"
                                                    onChange={(e) => {
                                                        const newValue = parseFloat(e.target.value);
                                                        setAdsetBidAmounts(prev => ({
                                                            ...prev,
                                                            [adSet._id]: newValue,
                                                        }));
                                                    }}
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>

                        <Box pt={8}>
                            <Flex gap={4}>
                                <Flex>
                                    <Text fontWeight="bold">Campaign budget</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    {campaignData?.daily_budget ? 'With a daily budget, we may spend more on some days and less on others, but will maintain an average daily budget.' : 'With a lifetime budget, we may spend more on some days and less on others, but will maintain an average lifetime budget.'}
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" m={1} />
                                    </Tooltip>
                                </Flex>
                                <Flex w={'120px'} direction='column' gap={1}>
                                    {formik.values.budget === 'daily_budget' ? (
                                        <InputGroup>
                                            <Input
                                                name="daily_budget"
                                                placeholder={currencySymbolMap(formik?.values?.currency)}
                                                bg="#fff"
                                                type="number"
                                                value={String(formik.values.daily_budget)}
                                                onChange={(e) => formik.setFieldValue('daily_budget', e.target.value)}
                                                onBlur={formik.handleBlur}
                                                borderRight={'none'}
                                                size={'sm'}
                                            />
                                            <InputRightAddon height={'32px'} fontSize={'10px'} bg='none' borderRadius={'none'}>
                                                {formik?.values?.currency}
                                            </InputRightAddon>
                                        </InputGroup>
                                    ) : null}
                                    {formik.values.budget === 'lifetime_budget' ? (
                                        <InputGroup>
                                            <Input
                                                name="lifetime_budget"
                                                placeholder={currencySymbolMap(formik?.values?.currency)}
                                                bg="#fff"
                                                type="number"
                                                value={String(formik.values.lifetime_budget)}
                                                onChange={(e) => formik.setFieldValue('lifetime_budget', e.target.value)}
                                                onBlur={formik.handleBlur}
                                                borderRadius={'md'}
                                                size={'sm'}
                                            />
                                            <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                {formik?.values?.currency}
                                            </InputRightAddon>
                                        </InputGroup>
                                    ) : null}
                                    <Flex>
                                        <Text fontSize={'12px'} color='gray' fontWeight='semibold'>{formik.values.budget === 'daily_budget' ? 'daily budget' : 'lifetime budget'}</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Box>
                    </ModalBody>

                    <ModalFooter>
                        <Flex borderTop="1px" borderColor="gray.100" pt={2} justifyContent="space-between" w="100%">
                            <Button onClick={onBidModalClose} size="sm">
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    formik.handleSubmit();
                                    onBidModalClose();
                                    onClose();
                                }}
                                size="sm"
                                colorScheme="brand"
                            >
                                Save
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default CampaignForm;
